/**
 * Citation gap derivation — shared between the public AI Visibility report
 * (`@marketing/ai-credit-report`) and the earned-media L2 drill-down.
 *
 * Extracted verbatim from the report's `lib/citation-intelligence.ts` so both
 * surfaces name the same gap categories, priorities and thresholds. Nothing
 * here fetches: callers hand in the citations (or per-source rollups expanded
 * into citation-shaped rows) and the brand's own counts.
 *
 * Copy discipline: `recommendation` is a CANNED per-category play, never a
 * claim about what a page says. Anything numeric a UI renders must come from
 * the counts on the returned gap, not from this string.
 */

import {
	isNonPitchableAuthority,
	isOwnDomainCitation,
} from "@repo/core/client";

/** Competitor citation with URL and metadata. */
export interface CompetitorCitation {
	url: string;
	title?: string | null;
	domain: string;
	mediaType?: string | null;
	authorityType?: string | null;
	contentFormat?: string | null;
	isListicle?: boolean | null;
}

/** Citation gap with the specific competitor citations behind it. */
export interface CitationGap {
	type: "media_type" | "authority_type" | "content_format";
	category: string;
	displayName: string;
	competitorCount: number;
	brandCount: number;
	priority: "high" | "medium" | "low";
	recommendation: string;
	/** Actual citations for this gap (deduped by domain, max 5). */
	competitorCitations: CompetitorCitation[];
}

/**
 * The only part of the report's `CitationIntelligenceStats` the gap logic
 * reads. Declared structurally so the report can pass its full stats object
 * and the dashboard can pass a small derived one.
 *
 * Keys are the report's camelCase breakdown keys (`reviewPlatform`,
 * `tradeMedia`, …), not the raw snake_case DB values.
 */
export interface CitationGapStats {
	mediaTypes: Record<string, number>;
	authorityTypes: Record<string, number>;
	contentFormats: Record<string, number>;
}

/** Extract a bare hostname from a URL, falling back to the raw string. */
export function extractDomain(url: string): string {
	try {
		const parsed = new URL(url);
		return parsed.hostname.replace(/^www\./, "");
	} catch {
		return url;
	}
}

/** Get competitor citations (non-owned) from discovery results. */
export function getCompetitorCitations(
	discoveryResults: any[],
	// The subject's domain, or null when it owns no website. Without this guard a
	// domainless subject's citations are ALL flagged `isOurDomain`, so every one
	// is skipped and the competitor list comes back empty.
	brandDomain: string | null,
): CompetitorCitation[] {
	const citations: CompetitorCitation[] = [];
	const seenUrls = new Set<string>();

	for (const execution of discoveryResults) {
		for (const platformExecution of execution.platformExecutions || []) {
			for (const citation of platformExecution.citations || []) {
				// Skip owned/brand citations, plus publishers that never take a
				// pitch (.gov, academic, encyclopedia) — these citations become
				// the report's PR gap recommendations, and those outlets can't
				// act on one.
				if (
					isOwnDomainCitation(citation.isOurDomain, brandDomain) ||
					citation.mediaType === "owned" ||
					isNonPitchableAuthority(citation.authorityType)
				) {
					continue;
				}

				// Deduplicate by URL
				if (seenUrls.has(citation.url)) {
					continue;
				}
				seenUrls.add(citation.url);

				citations.push({
					url: citation.url,
					title: citation.title,
					domain: extractDomain(citation.url),
					mediaType: citation.mediaType,
					authorityType: citation.authorityType,
					contentFormat: citation.contentFormat,
					isListicle: citation.isListicle,
				});
			}
		}
	}

	return citations;
}

/** Group competitor citations by a specific field. */
export function groupCitationsByField(
	citations: CompetitorCitation[],
	field: "mediaType" | "authorityType" | "contentFormat",
): Record<string, CompetitorCitation[]> {
	const groups: Record<string, CompetitorCitation[]> = {};

	for (const citation of citations) {
		const value = citation[field] || "unclassified";
		if (!groups[value]) {
			groups[value] = [];
		}
		groups[value].push(citation);
	}

	return groups;
}

/**
 * Calculate citation intelligence gaps for recommendations.
 * Identifies areas where competitors are cited but the brand is not.
 */
export function identifyCitationGaps(
	allCitationsStats: CitationGapStats,
	brandCitationsStats: CitationGapStats | null,
	competitorCitations?: CompetitorCitation[],
): CitationGap[] {
	const gaps: CitationGap[] = [];

	// Group competitor citations by category for detailed recommendations
	const byMediaType = competitorCitations
		? groupCitationsByField(competitorCitations, "mediaType")
		: {};
	const byAuthorityType = competitorCitations
		? groupCitationsByField(competitorCitations, "authorityType")
		: {};
	const byContentFormat = competitorCitations
		? groupCitationsByField(competitorCitations, "contentFormat")
		: {};

	// Helper to get unique citations by domain (max 5)
	const getUniqueCitationsByDomain = (
		citations: CompetitorCitation[],
		max = 5,
	): CompetitorCitation[] => {
		const seenDomains = new Set<string>();
		const result: CompetitorCitation[] = [];
		for (const c of citations) {
			if (!seenDomains.has(c.domain) && result.length < max) {
				seenDomains.add(c.domain);
				result.push(c);
			}
		}
		return result;
	};

	// Media type: Review platforms
	const reviewPlatformCitations = [
		...(byMediaType.review_platform || []),
		...(byMediaType.reviewPlatform || []),
	];
	if (reviewPlatformCitations.length > 0) {
		const uniqueCitations = getUniqueCitationsByDomain(
			reviewPlatformCitations,
		);
		const siteNames = uniqueCitations
			.map((c) => c.domain)
			.slice(0, 3)
			.join(", ");
		gaps.push({
			type: "media_type",
			category: "review_platform",
			displayName: "Review Platforms",
			competitorCount: reviewPlatformCitations.length,
			brandCount: brandCitationsStats?.mediaTypes.reviewPlatform || 0,
			priority: "high",
			recommendation: `AI models are citing review platforms like ${siteNames}. Ensure your brand has an active presence on these sites with customer reviews and testimonials.`,
			competitorCitations: uniqueCitations,
		});
	}

	// Media type: Earned press
	const earnedPressCitations = [
		...(byMediaType.earned_press || []),
		...(byMediaType.earnedPress || []),
	];
	if (
		earnedPressCitations.length > 0 &&
		allCitationsStats.mediaTypes.owned === 0
	) {
		const uniqueCitations =
			getUniqueCitationsByDomain(earnedPressCitations);
		const siteNames = uniqueCitations
			.map((c) => c.domain)
			.slice(0, 3)
			.join(", ");
		gaps.push({
			type: "media_type",
			category: "earned_press",
			displayName: "Press Coverage",
			competitorCount: earnedPressCitations.length,
			brandCount: brandCitationsStats?.mediaTypes.earnedPress || 0,
			priority: "high",
			recommendation: `Competitors are being cited in press coverage on ${siteNames}. Develop a PR strategy to gain coverage in these and similar publications.`,
			competitorCitations: uniqueCitations,
		});
	}

	// Authority type: Trade media
	const tradeMediaCitations = [
		...(byAuthorityType.trade_media || []),
		...(byAuthorityType.tradeMedia || []),
	];
	if (tradeMediaCitations.length > 0) {
		const uniqueCitations = getUniqueCitationsByDomain(tradeMediaCitations);
		const siteNames = uniqueCitations
			.map((c) => c.domain)
			.slice(0, 3)
			.join(", ");
		gaps.push({
			type: "authority_type",
			category: "trade_media",
			displayName: "Trade Publications",
			competitorCount: tradeMediaCitations.length,
			brandCount: brandCitationsStats?.authorityTypes.tradeMedia || 0,
			priority: "medium",
			recommendation: `Industry publications like ${siteNames} are citing your competitors. Seek guest posts, thought leadership content, or press coverage in these outlets.`,
			competitorCitations: uniqueCitations,
		});
	}

	// Authority type: Major media
	const majorMediaCitations = [
		...(byAuthorityType.major_media || []),
		...(byAuthorityType.majorMedia || []),
	];
	if (majorMediaCitations.length > 0) {
		const uniqueCitations = getUniqueCitationsByDomain(majorMediaCitations);
		const siteNames = uniqueCitations
			.map((c) => c.domain)
			.slice(0, 3)
			.join(", ");
		gaps.push({
			type: "authority_type",
			category: "major_media",
			displayName: "Major Media Outlets",
			competitorCount: majorMediaCitations.length,
			brandCount: brandCitationsStats?.authorityTypes.majorMedia || 0,
			priority: "high",
			recommendation: `Major media outlets like ${siteNames} are featuring your competitors. Consider a PR campaign targeting these high-authority publications.`,
			competitorCitations: uniqueCitations,
		});
	}

	// Content format: Listicles
	const listicleCitations =
		competitorCitations?.filter(
			(c) => c.isListicle || c.contentFormat === "listicle",
		) || [];
	if (listicleCitations.length > 0) {
		const uniqueCitations = getUniqueCitationsByDomain(listicleCitations);
		const siteNames = uniqueCitations
			.map((c) => c.domain)
			.slice(0, 3)
			.join(", ");
		gaps.push({
			type: "content_format",
			category: "listicle",
			displayName: "Listicles & Rankings",
			competitorCount: listicleCitations.length,
			brandCount: brandCitationsStats?.contentFormats.listicle || 0,
			priority: "high",
			recommendation: `"Best of" lists and rankings on ${siteNames} are citing competitors. Reach out to these publishers to be included in their roundups.`,
			competitorCitations: uniqueCitations,
		});
	}

	// Content format: Reviews
	const reviewCitations = byContentFormat.review || [];
	if (reviewCitations.length > 0) {
		const uniqueCitations = getUniqueCitationsByDomain(reviewCitations);
		const siteNames = uniqueCitations
			.map((c) => c.domain)
			.slice(0, 3)
			.join(", ");
		gaps.push({
			type: "content_format",
			category: "review",
			displayName: "Review Content",
			competitorCount: reviewCitations.length,
			brandCount: brandCitationsStats?.contentFormats.review || 0,
			priority: "medium",
			recommendation: `Review content on ${siteNames} features your competitors. Encourage customers to leave reviews and seek product reviews from these publications.`,
			competitorCitations: uniqueCitations,
		});
	}

	// Deduplicate gaps by category (some may overlap)
	const seenCategories = new Set<string>();
	const uniqueGaps = gaps.filter((gap) => {
		const key = `${gap.type}-${gap.category}`;
		if (seenCategories.has(key)) {
			return false;
		}
		seenCategories.add(key);
		return true;
	});

	return uniqueGaps.sort((a, b) => {
		const priorityOrder = { high: 0, medium: 1, low: 2 };
		return priorityOrder[a.priority] - priorityOrder[b.priority];
	});
}
